/* eslint-disable jsx-a11y/anchor-is-valid */
import {
    getAuthorityEndDate,
    getClientNames,
    InternalPurchaserSchema,
    PurchaserListFilters,
} from '@buyerflo/core/schemas/purchaser'
import { StatusBadge } from '@buyerflo/ui/components/StatusBadge'
import { getPurchaserBudget, getPurchaserRegion } from '@buyerflo/ui/utils/purchaser'
import { FileCheck } from '@tamagui/lucide-icons-2'
import { useMutation } from '@tanstack/react-query'
import { usePurchaserConfigApi, usePurchasersApi, useUserName } from 'apiProviders'
import { EmailRounded, UploadFileRounded } from 'components/icons'
import Menu from 'components/menu'
import { differenceInDays, format } from 'date-fns'
import { useSessionStorageState } from 'helperFunctions/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { ScrollView, Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { useOptionalOnboarding } from '../../onboarding/OnboardingContext'
import SortableHeader, { Sort } from './sortableHeader'

const AuthorityEndRender = ({ purchaser }: { purchaser: InternalPurchaserSchema }) => {
    const { purchaserStatus } = purchaser
    const authorityEnd = getAuthorityEndDate(purchaser)
    if (authorityEnd == undefined) {
        return ''
    }

    const daysToExpiry = differenceInDays(authorityEnd, new Date())

    return (
        <Text
            fontSize="$2"
            fontFamily="$mono"
            fontWeight="500"
            color={
                daysToExpiry && (purchaserStatus == 'searching' || purchaserStatus == 'consultation')
                    ? daysToExpiry < 1
                        ? '$statusDanger'
                        : daysToExpiry < 5
                          ? '$statusWarn'
                          : '$colorSoft'
                    : '$colorSoft'
            }
        >
            {format(authorityEnd, 'dd/MM/yyyy')}
        </Text>
    )
}

const ActionChip = ({ purchaser, actionId }: { purchaser: InternalPurchaserSchema; actionId: string }) => {
    const trpc = useTRPC()
    const updateAdditionalDetailsMutation = useMutation(
        trpc.purchaser.updateAdditionalDetails.mutationOptions(),
    )
    const purchasersApi = usePurchasersApi()
    const purchaserConfigApi = usePurchaserConfigApi()

    const [loading, setLoading] = useState(false)

    if (loading) {
        return <Spinner size="small" />
    }

    if (!purchaserConfigApi.data) {
        return <Spinner size="small" />
    }

    const configs = purchaserConfigApi.data
    const config = configs.find(x => x.id == actionId)

    return (
        <Menu>
            <Menu.Trigger asChild>
                <View
                    backgroundColor="$backgroundRaised"
                    borderWidth={1}
                    borderColor="$borderColor"
                    paddingHorizontal="$2.5"
                    paddingVertical="$1"
                    borderRadius="$3"
                    cursor="pointer"
                    onPress={e => e.stopPropagation()}
                    hoverStyle={{ opacity: 0.85 }}
                >
                    <Text fontSize="$2" fontWeight="500">
                        {purchaser.additionalDetails?.[actionId] ??
                            config?.configValues[0].value ??
                            'Loading...'}
                    </Text>
                </View>
            </Menu.Trigger>
            <Menu.Portal>
                <Menu.Content>
                    <View onPress={e => e.stopPropagation()}>
                        {config?.configValues.map(value => (
                            <Menu.Item
                                key={value.value}
                                onPress={async e => {
                                    e.stopPropagation()
                                    setLoading(true)
                                    await updateAdditionalDetailsMutation.mutateAsync({
                                        purchaserId: purchaser.purchaserId,
                                        additionalDetails: {
                                            ...purchaser.additionalDetails,
                                            [actionId]: value.value,
                                        },
                                    })
                                    await purchasersApi.refresh()
                                    setLoading(false)
                                }}
                            >
                                <Menu.ItemTitle>{value.value}</Menu.ItemTitle>
                            </Menu.Item>
                        ))}
                    </View>
                </Menu.Content>
            </Menu.Portal>
        </Menu>
    )
}

type ColumnKey = 'status' | 'agent' | 'budget' | 'region' | 'agentAppointmentForm' | 'authorityEnd' | 'action'

type Column =
    | { key: 'status' | 'agent' | 'budget' | 'region' | 'agentAppointmentForm' | 'authorityEnd' }
    | { key: 'action'; actionId: string }

const getColumnName = (key: ColumnKey): string => {
    switch (key) {
        case 'status':
            return 'Status'
        case 'agent':
            return 'Agent'
        case 'budget':
            return 'Budget'
        case 'region':
            return 'Region'
        case 'agentAppointmentForm':
            return 'Form 6'
        case 'authorityEnd':
            return 'Authority End'
        case 'action':
            return 'Action'
        default:
            return key
    }
}

const Muted = ({ children, mono }: { children: React.ReactNode; mono?: boolean }) => (
    <Text
        fontSize="$2"
        color="$colorSoft"
        fontFamily={mono ? '$mono' : undefined}
        numberOfLines={1}
    >
        {children}
    </Text>
)

const RenderCell = ({ col, row }: { col: Column; row: InternalPurchaserSchema }) => {
    const userName = useUserName()

    switch (col.key) {
        case 'status':
            return <StatusBadge status={row.purchaserStatus} />
        case 'agent':
            return (
                <XStack
                    gap="$1.5"
                    flexWrap="wrap"
                >
                    {row.agents?.map(agent => (
                        <View
                            key={agent.userId}
                            backgroundColor="$backgroundRaised"
                            borderWidth={1}
                            borderColor="$borderColor"
                            paddingHorizontal="$2.5"
                            paddingVertical="$1"
                            borderRadius="$3"
                        >
                            <Text fontSize="$2" fontWeight="500">
                                {userName(agent.userId)}
                            </Text>
                        </View>
                    ))}
                </XStack>
            )
        case 'budget':
            return <Muted mono>{getPurchaserBudget(row) || '—'}</Muted>
        case 'region':
            return <Muted>{getPurchaserRegion(row) || '—'}</Muted>
        case 'agentAppointmentForm':
            return (
                <>
                    {row.agentAppointmentForm?.esign?.formStatus == 'sent' && <EmailRounded size={18} />}
                    {row.agentAppointmentForm?.esign?.formStatus == 'completed' && (
                        <FileCheck
                            color="$primary"
                            size={18}
                        />
                    )}
                    {row.agentAppointmentForm?.esign?.formStatus == 'manual upload' && (
                        <UploadFileRounded
                            color="$primary"
                            size={18}
                        />
                    )}
                </>
            )
        case 'authorityEnd':
            return <AuthorityEndRender purchaser={row} />
        case 'action':
            return (
                <ActionChip
                    purchaser={row}
                    actionId={col.actionId}
                />
            )
    }
}

const defaultColumns: Column[] = [
    { key: 'status' },
    { key: 'agent' },
    { key: 'budget' },
    { key: 'region' },
    { key: 'agentAppointmentForm' },
    { key: 'authorityEnd' },
]

// Column flex values for consistent widths
const columnFlex: Record<ColumnKey, number> = {
    status: 1,
    agent: 1.3,
    budget: 1,
    region: 1.3,
    agentAppointmentForm: 0.7,
    authorityEnd: 1,
    action: 1,
}

const sortStringFor: Partial<Record<ColumnKey, string>> = {
    status: 'purchaserStatus',
    agent: 'userNames',
    budget: 'budgetRange',
    region: 'region',
    authorityEnd: 'authorityEndDate',
}

export default function PurchasersTable({
    purchasersApi,
}: {
    purchasersApi: ReturnType<typeof usePurchasersApi>
}) {
    const navigate = useNavigate()
    const onboarding = useOptionalOnboarding()
    const firstPurchaserId = onboarding?.testPurchaserId ?? onboarding?.progress?.firstPurchaserId

    const rows = purchasersApi.data ?? []

    const [sort, setSort] = useSessionStorageState<Sort | undefined>('purchasers_sort', {
        sort: purchasersApi.params.sort ?? '',
        sortDirection: purchasersApi.params.sortDirection ?? 'desc',
    })

    useEffect(() => {
        const searchParams: Partial<PurchaserListFilters> = {
            sort: sort?.sort,
            sortDirection: sort?.sortDirection,
        }

        purchasersApi.setParams(x => ({
            ...x,
            ...searchParams,
        }))
    }, [sort])

    return (
        <YStack
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius={14}
            display="none"
            $sm={{ display: 'flex' }}
            width="100%"
            flex={1}
            minHeight={0}
            overflow="hidden"
            marginBottom="$8"
            backgroundColor="$backgroundStrong"
        >
            {/* Header row */}
            <XStack
                backgroundColor="$background"
                borderBottomWidth={1}
                borderBottomColor="$borderColor"
                paddingVertical="$3"
                paddingHorizontal="$4"
            >
                <View
                    flex={2}
                    paddingHorizontal="$2"
                >
                    <SortableHeader
                        sort={sort}
                        setSort={setSort}
                        sortString="buyerNames"
                    >
                        Client
                    </SortableHeader>
                </View>
                {defaultColumns.map(col => (
                    <View
                        key={col.key}
                        flex={columnFlex[col.key]}
                        paddingHorizontal="$2"
                    >
                        {col.key == 'agentAppointmentForm' ? (
                            <Text
                                fontWeight="600"
                                fontSize="$2"
                                color="$colorMuted"
                                textTransform="uppercase"
                                letterSpacing={0.5}
                            >
                                {getColumnName(col.key)}
                            </Text>
                        ) : (
                            <SortableHeader
                                sort={sort}
                                setSort={setSort}
                                sortString={sortStringFor[col.key] ?? col.key}
                            >
                                {getColumnName(col.key)}
                            </SortableHeader>
                        )}
                    </View>
                ))}
            </XStack>

            {/* Data rows */}
            <ScrollView flex={1}>
                {rows.map((row, index) => (
                    <XStack
                        key={row.purchaserId}
                        paddingVertical="$3"
                        paddingHorizontal="$4"
                        alignItems="center"
                        cursor="pointer"
                        animation="quick"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        pressStyle={{ backgroundColor: '$backgroundPress' }}
                        borderBottomWidth={index < rows.length - 1 ? 1 : 0}
                        borderBottomColor="$borderColor"
                        {...(row.purchaserId === firstPurchaserId ? { 'data-tour': 'first-purchaser-row' } : {})}
                        onPress={() => navigate(`${row.purchaserId}/client`)}
                    >
                        <View
                            flex={2}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$3"
                                fontWeight="600"
                                numberOfLines={1}
                            >
                                {getClientNames(row.buyers)}
                            </Text>
                        </View>
                        {defaultColumns.map(col => (
                            <View
                                key={col.key}
                                flex={columnFlex[col.key]}
                                paddingHorizontal="$2"
                                overflow="hidden"
                            >
                                <RenderCell
                                    col={col}
                                    row={row}
                                />
                            </View>
                        ))}
                    </XStack>
                ))}
            </ScrollView>
        </YStack>
    )
}
